import { useCallback, useSyncExternalStore } from "react";
import { APP_LOCALES, AppLocale, localeNativeLabels } from "./app-locale";
import { getLocale, initializeLocale, setLocale, subscribeLocale } from "./locale-store";
import { translate, translateAround, type TranslationParams } from "./translate";
import type { TranslationKey } from "./messages";

export type UseI18nResult = {
  locale: AppLocale;
  locales: readonly AppLocale[];
  localeLabels: Record<AppLocale, string>;
  changeLocale: (locale: AppLocale) => void;
  t: (key: TranslationKey, params?: TranslationParams) => string;
  tAround: (key: TranslationKey, placeholderName: string, params?: TranslationParams) => [string, string];
};

/**
 * 화면에서 문자열 사전을 사용하기 위한 훅.
 * 언어가 바뀌면 구독 중인 컴포넌트만 다시 렌더링된다.
 */
export function useI18n(): UseI18nResult {
  const locale = useSyncExternalStore(subscribeLocale, getLocale, initializeLocale);

  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams) => translate(key, params, locale),
    [locale],
  );

  const tAround = useCallback(
    (key: TranslationKey, placeholderName: string, params?: TranslationParams) =>
      translateAround(key, placeholderName, params, locale),
    [locale],
  );

  return {
    locale,
    locales: APP_LOCALES,
    localeLabels: localeNativeLabels,
    changeLocale: setLocale,
    t,
    tAround,
  };
}
